import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Typography from '@material-ui/core/Typography';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import clsx from 'clsx';
import { StatusManager } from 'common/components/StatusManager';
import { AI_ENDPOINTS } from 'features/ai-generators/constants';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { DateFormats } from 'UI/constants/defaults';
import { toLocalTime } from 'UI/utils';

import { ROLEPLAY_FEEDBACK_STATUSES } from '../constants';

import { ReportIssue } from './ReportIssue';
import { useStyles } from './RoleplayCallInsights.styled';

const COPIES = strings.ai.generators.roleplay.feedbackDrawer.tabs.aiInsights;

export const RoleplayCallInsights = ({ callId }) => {
  const classes = useStyles();
  const { refreshData, state } = useFetchWithStatusV2({
    apiInstance: SECONDARY_API_CLIENT,
    endpoint: `${AI_ENDPOINTS.roleplayCallDetails.replace(':id', callId)}`
  });

  const data = state?.results || {};
  const isCompleted = data?.status?.id === ROLEPLAY_FEEDBACK_STATUSES.completed;

  const issueComment = data?.issueComment;
  const issueCommentDate = data?.issueCommentedAt
    ? toLocalTime(data?.issueCommentedAt).format(DateFormats.SimpleDateTime)
    : '-';
  const whatWentWellItems =
    data?.whatWentWell?.length > 0 ? data?.whatWentWell : [COPIES.noFeedback];
  const goalsItems = Array.isArray(data?.goals) ? data.goals : [];
  const areasForImprovementItems =
    data?.areasForImprovement?.length > 0 ? data?.areasForImprovement : null;

  return (
    <StatusManager
      status={state.status}
      slots={{
        Error: <EmptyPlaceholder title={COPIES.error} subtitle={state.error} />,
        Loading: (
          <Box alignItems="center" display="flex" height="100%" justifyContent="center">
            <CircularProgress />
          </Box>
        )
      }}
    >
      {isCompleted ? (
        <Box display="flex" flexDirection="column" gridGap={36} p={3}>
          <Box display="flex" flexDirection="column" gridGap={20}>
            <Typography variant="subtitle1">{COPIES.feedbackSummary}</Typography>

            <Typography variant="body2">{data?.feedbackSummary || COPIES.noFeedback}</Typography>
          </Box>

          <Box display="flex" flexDirection="column" gridGap={20}>
            <Typography variant="subtitle1">{COPIES.whatWentWell}</Typography>

            <List className={clsx(classes.list, classes.listPadding)} disablePadding>
              {whatWentWellItems.map(item => (
                <ListItem
                  alignItems="flex-start"
                  className={classes.listItem}
                  dense
                  disableGutters
                  key={item}
                >
                  <Typography variant="body2">{item}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box display="flex" flexDirection="column" gridGap={20}>
            <Typography variant="subtitle1">
              {strings.formatString(COPIES.goals, { goalsMet: data?.goalsMet || 0 })}
            </Typography>

            <List className={classes.list} disablePadding>
              {goalsItems.length > 0 ? (
                goalsItems.map(({ goal, reached }) => (
                  <ListItem dense disableGutters key={goal}>
                    <ListItemIcon className={classes.listItemIcon}>
                      {reached ? (
                        <CheckCircleRoundedIcon className={classes.successIcon} />
                      ) : (
                        <CancelRoundedIcon className={classes.errorIcon} />
                      )}
                    </ListItemIcon>
                    <Typography color={reached ? 'textPrimary' : 'textSecondary'} variant="body2">
                      {goal}
                    </Typography>
                  </ListItem>
                ))
              ) : (
                <ListItem dense disableGutters>
                  <Typography variant="body2">{COPIES.noFeedback}</Typography>
                </ListItem>
              )}
            </List>
          </Box>

          {areasForImprovementItems && (
            <Alert severity="warning">
              <AlertTitle>{COPIES.whatToImprove}</AlertTitle>
              <List className={classes.list} disablePadding>
                {areasForImprovementItems.map(item => (
                  <ListItem
                    alignItems="flex-start"
                    className={classes.listItem}
                    dense
                    disableGutters
                    key={item}
                  >
                    <Typography variant="body2">{item}</Typography>
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          <ReportIssue
            callId={callId}
            issueComment={issueComment}
            issueCommentDate={issueCommentDate}
            onRefreshData={refreshData}
          />
        </Box>
      ) : (
        <EmptyPlaceholder title={COPIES.emptyState.title} subtitle={COPIES.emptyState.subtitle}>
          <Button color="primary" onClick={refreshData} startIcon={<RefreshOutlinedIcon />}>
            {COPIES.emptyState.actionButton}
          </Button>
        </EmptyPlaceholder>
      )}
    </StatusManager>
  );
};
